import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Scale, Users, HeartHandshake, Languages, ShieldCheck, Sparkles, MapPin, Award, MessageCircle, Star } from "lucide-react";
import api from "@/lib/api";
import imageUrl from "@/lib/imageUrl";
import { Button } from "@/components/ui/button";
import { JudgmentCard } from "@/components/JudgmentCard";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useHelp } from "@/components/HelpWidget";
import { LegalQuestionSearch } from "@/components/LegalQuestionSearch";
import { UMBRELLA_GROUPS } from "@/lib/categories";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import Seo from "@/components/Seo";
import { PAGE_SEO, webSiteJsonLd, orgJsonLd } from "@/lib/seo";

const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;

function LawyerPreviewCard({ lw, i, lang, L }) {
  const bio = L(lw, "bio") || "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(i * 0.07, 0.3) }}
      className="card-hover flex h-full flex-col rounded-2xl border bg-card p-5"
      data-testid={`home-lawyer-card-${lw.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-14 w-14 shrink-0">
          <AvatarImage
            src={imageUrl(lw.photo, { w: 160 })}
            alt={lw.name}
            width={56}
            height={56}
            loading="lazy"
            decoding="async"
            className="object-cover"
          />
          <AvatarFallback>{lw.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="font-serif text-base font-bold leading-tight">{lw.name}</h3>
            {lw.is_founder && (
              <Badge className="gap-1 bg-amber-400 text-amber-950 hover:bg-amber-400 text-[10px] px-1.5">
                <Star className="h-2.5 w-2.5" /> {lang === "hi" ? "संस्थापक" : "Founder"}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs font-medium text-primary">{L(lw, "specialization")}</p>
          {lw.online_available && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {lang === "hi" ? "ऑनलाइन उपलब्ध" : "Online"}
            </span>
          )}
        </div>
      </div>

      {bio && <p className="mt-3 line-clamp-3 text-xs text-muted-foreground leading-relaxed">{bio}</p>}

      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {lw.experience_years ? (
          <p className="flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> {lw.experience_years}+ {lang === "hi" ? "वर्ष अनुभव" : "yrs experience"}</p>
        ) : null}
        {lw.location ? (
          <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {lw.location}</p>
        ) : null}
      </div>

      <div className="mt-auto flex items-center gap-2 pt-4">
        <Button asChild size="sm" className="flex-1 text-xs" data-testid={`home-lawyer-view-${lw.id}`}>
          <Link to={`/supreme-court-lawyers/${lw.id}`}>
            {lang === "hi" ? "प्रोफ़ाइल देखें" : "View Profile"} <ArrowRight className="ml-1 h-3 w-3" />
          </Link>
        </Button>
        {(lw.whatsapp || lw.phone) && (
          <Button asChild variant="outline" size="icon" className="shrink-0 h-8 w-8" aria-label="WhatsApp">
            <a href={waLink(lw.whatsapp || lw.phone)} target="_blank" rel="noreferrer">
              <MessageCircle className="h-3.5 w-3.5" />
            </a>
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function Home() {
  const { t, L, lang } = useLanguage();
  const { content } = useSiteContent();
  const { open } = useHelp();

  const { data: latest, isLoading } = useQuery({ queryKey: ["judgments", "home"], queryFn: async () => (await api.get("/public/judgments?limit=6")).data });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/public/stats")).data });
  const { data: team } = useQuery({ queryKey: ["team", "home"], queryFn: async () => (await api.get("/public/team")).data });
  const { data: lawyers } = useQuery({ queryKey: ["sc-lawyers", "home"], queryFn: async () => (await api.get("/public/lawyers")).data });

  const features = [
    { icon: BookOpen, en: "Plain-language summaries", hi: "सरल भाषा में सारांश", color: "bg-teal-100 text-teal-700" },
    { icon: Languages, en: "English & Hindi", hi: "अंग्रेज़ी और हिंदी", color: "bg-violet-100 text-violet-700" },
    { icon: HeartHandshake, en: "Free lawyer guidance", hi: "निःशुल्क वकील मार्गदर्शन", color: "bg-amber-100 text-amber-700" },
    { icon: ShieldCheck, en: "Editor-verified", hi: "संपादक-सत्यापित", color: "bg-rose-100 text-rose-700" },
  ];

  return (
    <div>
      <Seo {...PAGE_SEO.home} jsonLd={[webSiteJsonLd(), orgJsonLd()]} />
      {/* Hero */}
      <section className="grad-hero relative overflow-hidden">
        <div className="pointer-events-none absolute -left-16 top-8 h-72 w-72 rounded-full bg-teal-300/40 blur-3xl animate-blob" />
        <div className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full bg-amber-300/40 blur-3xl animate-blob anim-delay-3s" />
        <div className="pointer-events-none absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-violet-300/40 blur-3xl animate-blob anim-delay-6s" />

        <div className="relative mx-auto grid w-full max-w-6xl gap-8 px-4 py-16 sm:px-6 md:grid-cols-2 md:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Brand name — big, bold, unmistakable */}
            <h1 className="font-serif text-6xl font-extrabold tracking-tight text-foreground sm:text-7xl lg:text-8xl" data-testid="brand-heading">
              Litigation India
              <span className="mt-2 block font-sans text-lg font-semibold text-muted-foreground sm:text-xl lg:text-2xl">
                Insight First, Action Next
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground reading-prose">{L(content, "tagline") || "Supreme Court judgments, explained simply — and free guidance for every litigant."}</p>
            <LegalQuestionSearch className="mt-6 max-w-xl" testId="home-legal-question-search" />
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="grad-cta text-white hover:opacity-90" data-testid="hero-updates-cta">
                <Link to="/updates">{t("nav_updates")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-advice-cta">
                <Link to="/advice"><HeartHandshake className="mr-1 h-4 w-4" /> {t("nav_advice")}</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-primary" data-testid="hero-know-more">
                <Link to="/about"><Sparkles className="mr-1 h-4 w-4" /> {t("learn_more")}</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="flex items-center justify-center">
            <div className="grid w-full max-w-sm grid-cols-2 gap-3">
              {features.map((f, i) => (
                <motion.div key={i} whileHover={{ y: -4 }} className="rounded-2xl border bg-card p-4 shadow-sm">
                  <span className={cn("flex h-10 w-10 items-center justify-center rounded-xl", f.color)}><f.icon className="h-5 w-5" /></span>
                  <p className="mt-3 text-sm font-semibold">{lang === "hi" ? f.hi : f.en}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y bg-card">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-3 gap-4 px-4 py-8 sm:px-6">
          {[
            { n: stats?.judgments, label: lang === "hi" ? "प्रकाशित फैसले" : "Judgments published", icon: Scale, color: "text-teal-600" },
            { n: stats?.lawyers, label: lang === "hi" ? "सर्वोच्च न्यायालय अधिवक्ता" : "Supreme Court Advocates", icon: HeartHandshake, color: "text-amber-600" },
            { n: stats?.team, label: lang === "hi" ? "टीम सदस्य" : "Team members", icon: Users, color: "text-violet-600" },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <s.icon className={cn("h-6 w-6", s.color)} />
              <span className="mt-2 font-serif text-3xl font-bold">{s.n ?? "—"}</span>
              <span className="text-xs text-muted-foreground sm:text-sm">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Browse by category */}
      <section className="mx-auto w-full max-w-6xl px-4 pt-14 sm:px-6">
        <h2 className="font-serif text-2xl font-bold tracking-tight">{t("browse_by_area")}</h2>
        <p className="mt-1 text-muted-foreground">{t("browse_by_area_sub")}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          {UMBRELLA_GROUPS.map((g) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.name}
                to={`/updates?group=${encodeURIComponent(g.name)}`}
                data-testid="home-area-pill"
                className={cn("card-hover inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-opacity duration-150 hover:opacity-80", g.chip)}
              >
                <Icon className="h-4 w-4" /> {lang === "hi" ? g.nameHi : g.name}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Latest updates */}
      <section className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-serif text-3xl font-bold tracking-tight">{t("latest_updates")}</h2>
            <p className="mt-2 text-muted-foreground">{t("latest_updates_sub")}</p>
          </div>
          <Button asChild variant="ghost" className="hidden sm:inline-flex" data-testid="home-view-all-updates">
            <Link to="/updates">{t("view_all_updates")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)
            : latest?.items?.map((j, i) => <JudgmentCard key={j.id} judgment={j} index={i} />)}
        </div>
        <div className="mt-8 text-center sm:hidden"><Button asChild variant="outline"><Link to="/updates">{t("view_all_updates")}</Link></Button></div>
      </section>

      {/* Free advice CTA */}
      <section className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="grad-cta relative overflow-hidden rounded-3xl px-6 py-12 text-white sm:px-12">
          <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><HeartHandshake className="h-3.5 w-3.5" /> {t("pro_bono_badge")}</span>
            <h2 className="mt-3 font-serif text-3xl font-bold">{t("free_advice_title")}</h2>
            <p className="mt-3 text-white/90">{t("free_advice_sub")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" variant="secondary" data-testid="cta-talk-lawyer"><Link to="/advice">{t("explore_lawyers")}</Link></Button>
              <Button size="lg" variant="outline" onClick={open} className="border-white/40 bg-white/10 text-white hover:bg-white/20" data-testid="cta-get-callback">{t("request_callback")}</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Supreme Court Advocates preview */}
      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6" data-testid="home-lawyers-section">
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Scale className="h-4 w-4" /> {lang === "hi" ? "सर्वोच्च न्यायालय अधिवक्ता" : "Supreme Court Advocates"}
            </div>
            <h2 className="mt-1 font-serif text-3xl font-bold tracking-tight">
              {lang === "hi" ? "हमारे शीर्ष अधिवक्ता" : "Our Supreme Court Advocates"}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {lang === "hi"
                ? "अनुभवी अधिवक्ता जो ज़मानत, आपराधिक, संवैधानिक और सेवा मामलों में निःशुल्क मार्गदर्शन देते हैं।"
                : "Experienced advocates offering free pro bono guidance on bail, criminal, constitutional and service matters."}
            </p>
          </div>
          <Button asChild variant="ghost" className="hidden sm:inline-flex" data-testid="home-view-all-lawyers">
            <Link to="/supreme-court-lawyers">
              {lang === "hi" ? "सभी अधिवक्ता देखें" : "See all advocates"} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {!lawyers
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 w-full rounded-2xl" />)
            : (lawyers || []).slice(0, 4).map((lw, i) => (
                <LawyerPreviewCard key={lw.id} lw={lw} i={i} lang={lang} L={L} />
              ))}
        </div>
        {/* Mobile see more */}
        <div className="mt-6 text-center sm:hidden">
          <Button asChild variant="outline">
            <Link to="/supreme-court-lawyers">{lang === "hi" ? "सभी अधिवक्ता देखें" : "See all advocates"} <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      {/* Team teaser */}
      <section className="mx-auto w-full max-w-6xl px-4 pb-16 sm:px-6">
        <div className="flex items-end justify-between">
          <h2 className="font-serif text-3xl font-bold tracking-tight">{t("meet_team")}</h2>
          <Button asChild variant="ghost"><Link to="/team">{t("nav_team")} <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {team?.slice(0, 4).map((m) => (
            <motion.div key={m.id} whileHover={{ y: -4 }} className="rounded-2xl border bg-card p-5 text-center">
              <Avatar className="mx-auto h-20 w-20 ring-2 ring-accent">
                <AvatarImage src={imageUrl(m.photo, { w: 192 })} alt={m.name} width={80} height={80} loading="lazy" decoding="async" className="object-cover" />
                <AvatarFallback>{m.name?.[0]}</AvatarFallback>
              </Avatar>
              <h3 className="mt-3 font-serif text-lg font-semibold">{m.name}</h3>
              <p className="text-sm text-primary">{L(m, "role")}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
